import * as React from 'react';
import { __ } from 'react-i18n/lib';
import styled, { css } from 'styled-components';
import { rem } from 'polished';

import { Button } from '../General/Button';
import { OutsideClick } from '../../Defaults/OutsideClick';

import { SearchResultsViewer } from './SearchResultsViewer';
import { SvgIcon, IconType } from '../General/SvgIcon';
import { SearchFormInput } from './SearchFormInput';

// Interfaces
interface SearchFormProps {
  searchResults: {
    productsResult: any;
    categoriesResult: any;
    articlesResult: any;
  };
  searchTerm: string;
  redirectToSearchResultPage: (e, type?: string) => void;
  handleSearchTermChange: (e) => void;
  handleFocus: () => void;
  resultsDropdownVisible: boolean;
  hideResultViewer: (e) => void;
  isMobile?: boolean;
  partialSearchProductsIsFetching: boolean;
  partialSearchCategoriesIsFetching: boolean;
  partialSearchArticlesIsFetching: boolean;
  user: any;
  partialSearchCategoryProducts: any;
  clearSearchInput: () => void;
}

/*****************/

const SearchFormWrapper = styled.form`
  width: 100%;
  position: relative;

  ${({ theme }) => theme.media(0, 'sm')} {
  }
`;

const InnerWrapper = styled.div`
  max-width: ${({ theme }) => `${theme.grid.container.base}px`};
  width: 100%;
  margin: 0 auto;
  position: relative;
`;

const SearchFormContainer = styled.div`
  display: flex;
  position: relative;
  border-bottom: ${rem(2)} solid #eeeeee;

  ${({ theme }) => theme.mediab.l1150`
    padding: ${rem(0)} ${rem(28)};
  `}

  ${({ theme }) => theme.mediab.m810`
    padding: ${rem(0)} ${rem(16)};
  `}

  &:last-child {
    border-bottom: none;
  }
`;

const SearchResultsContainer = styled.div``;

const SearchFormInputWrapper = styled.div`
  flex: 1 0 auto;
  position: relative;
`;

const iconLikeCss = css`
  position: absolute;
  z-index: 1;
  width: ${rem(24)};
  height: ${rem(24)};
  fill: ${({ theme }) => theme.colors.textPrimary};
  top: 50%;
  transform: translateY(-50%);
`;

const SearchFormSvgIcon = styled(SvgIcon)`
  ${iconLikeCss};
  left: ${rem(0)};
`;

const ClearInputSvgIcon = styled(SvgIcon)`
  ${iconLikeCss};
  right: ${rem(0)};
`;

function SearchForm({
  searchResults,
  redirectToSearchResultPage,
  searchTerm,
  handleFocus,
  handleSearchTermChange,
  resultsDropdownVisible,
  hideResultViewer,
  isMobile = false,
  partialSearchProductsIsFetching,
  partialSearchCategoriesIsFetching,
  partialSearchArticlesIsFetching,
  user,
  partialSearchCategoryProducts,
  clearSearchInput,
}: SearchFormProps) {
  return (
    <SearchFormWrapper autoComplete="off">
      <OutsideClick handleFunction={hideResultViewer}>
        <SearchFormContainer>
          <InnerWrapper>
            <SearchFormSvgIcon
              width={24}
              height={24}
              icon={IconType.search}
              alt="Hľadať"
              title="Hľadať"
              cursor={'pointer'}
              onClick={redirectToSearchResultPage}
            />
            <SearchFormInputWrapper>
              <SearchFormInput
                isMobile={isMobile}
                name="q"
                type="text"
                placeholder={__(
                  'Zadajte názov produktu, článku alebo kategórie...',
                )}
                value={searchTerm}
                onFocus={handleFocus}
                onChange={handleSearchTermChange}
                autoFocus={true}
              />
            </SearchFormInputWrapper>
            <ClearInputSvgIcon
              width={24}
              height={24}
              icon={IconType.x}
              alt="Vymazať hľadaný výraz"
              title="Vymazať hľadaný výraz"
              cursor={'pointer'}
              onClick={clearSearchInput}
            />
          </InnerWrapper>
        </SearchFormContainer>
        <SearchResultsContainer>
          <InnerWrapper>
            <SearchResultsViewer
              partialSearchProductsIsFetching={partialSearchProductsIsFetching}
              partialSearchCategoriesIsFetching={
                partialSearchCategoriesIsFetching
              }
              partialSearchArticlesIsFetching={partialSearchArticlesIsFetching}
              visible={resultsDropdownVisible}
              searchResults={searchResults}
              searchTerm={searchTerm}
              redirectToResultPage={redirectToSearchResultPage}
              hideResultViewer={hideResultViewer}
              user={user}
              partialSearchCategoryProducts={partialSearchCategoryProducts}
            />
          </InnerWrapper>
        </SearchResultsContainer>
      </OutsideClick>
    </SearchFormWrapper>
  );
}

export { SearchForm };
